import { ROWS_PER_PAGE_SELECTOR } from '@constants';
import useFacilityProcessesList from '@hooks/useFacilityProcessesList';
import config from '@lib/config';
import {
  differenceInCalendarDays,
  differenceInMinutes,
  format,
  isBefore,
  parse,
  parseISO,
  startOfDay,
} from 'date-fns';
import { jwtDecode } from 'jwt-decode';
import { isEqual } from 'lodash-es';
import * as constants from './constants';
import dataPoints from './data-points';
export const getTokenExpirationDate = token => {
  const decoded = jwtDecode(token);
  if (!decoded.exp) {
    return null;
  }

  const date = new Date(0); // The 0 here is the key, which sets the date to the epoch
  date.setUTCSeconds(decoded.exp);
  return date;
};

export const isTokenExpired = token => {
  if (!token) return true;
  const date = getTokenExpirationDate(token);
  const offsetSeconds = 0;
  if (date === null) {
    return false;
  }

  return !(date.valueOf() > Date.now() + offsetSeconds * 1000);
};

export const isSessionExpired = sessionStartedAt => {
  if (!sessionStartedAt) return true;

  const accessTokenValidityInMins = 60 * 12; // 60 mins into 12 hours

  return (
    differenceInMinutes(Date.now(), sessionStartedAt) >=
    accessTokenValidityInMins
  );
};

export const isValidUrl = url => {
  try {
    new URL(url); // eslint-disable-line no-new
  } catch {
    return false;
  }

  return true;
};

export const generateId = () => {
  const d = Date.now();

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = ((Math.random() * 16 + d) % 16) | 0;

    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

export const humanFileSize = bytes => {
  const list = ['B', 'kB', 'MB', 'GB'];
  const factor = 1 / 1024;
  let unit = list.shift();
  let value = bytes;

  while (list.length > 0 && value * factor >= 1) {
    value *= factor;
    unit = list.shift();
  }

  return `${value.toFixed(0)}${unit}`;
};

export const checkBrowserSupport = () => {
  // Check if broswer supports .finally
  if (typeof Promise.prototype.finally !== 'function') return false;
  // If we have other things to check, please check here

  return true;
};

export const isNullOrUndefined = val => val === undefined || val === null;

export const isNullOrUndefinedOrEmpty = val =>
  val === undefined ||
  val === null ||
  (Array.isArray(val) && val.length < 1) ||
  val?.value === null;

export const getDefinedUrlSearchParams = object => {
  if (!object || typeof object !== 'object') {
    return '';
  }

  const definedParams = Object.entries(object).reduce(
    (obj, [key, val]) =>
      isNullOrUndefined(val) ? obj : { ...obj, [key]: val },
    {},
  );

  const searchParams = new URLSearchParams(definedParams);

  return searchParams.toString();
};

export const propValue = (obj, key, def) => {
  let p = 0;
  const keys = key.split ? key.split('.') : key;
  let current = obj;
  while (current && p < keys.length) current = current[keys[p++]];
  return current === undefined || p < keys.length
    ? def
    : current.root
      ? current.root
      : current;
};

export const filteredCountryMapping = countryCodes => {
  const filteredCountries = constants.countries.filter(c =>
    countryCodes.find(code => c.countryCode === code),
  );
  const countriesMapping = filteredCountries.reduce(
    (acc, obj) => ({ ...acc, [obj.countryCode]: obj.countryNameEn }),
    {},
  );
  return Object.keys(countriesMapping).map(k => ({
    key: k,
    text: countriesMapping[k],
  }));
};

export const getCapitalisedFirstLetterLowerCase = string => {
  if (string) {
    return string[0].toUpperCase() + string.slice(1).toLowerCase();
  }
};

export const transformFacilityProcesses = facilities => {
  const result = [];
  const tempFacilities = facilities ? [...facilities] : [];
  tempFacilities.forEach(facility => {
    const { productionProcessId, productionProcessName } = facility;
    const productionProcessTitle = {
      key: `${productionProcessId}_${productionProcessName}`, // To make sure facility's key and production process's key are unique
      title: productionProcessName,
      isDisabled: true,
      members: facility.children.map(child => child.text),
      memberKeys: facility.children.map(child => child.key).join(','),
    };
    result.push(productionProcessTitle);

    facility.children.forEach(child => {
      const facilityChild = {
        text: child.text,
        category: productionProcessId,
        key: child.key,
      };
      result.push(facilityChild);
    });
  });
  return result;
};
export const transformProductTypes = data => {
  const result = [];
  data.forEach(productType => {
    if (!productType.parentId) {
      const children = data.filter(type => type.parentId === productType.id);
      result.push({
        key: `${productType.id}_${productType.name}`,
        title: productType.name,
        isDisabled: true,
        members: children.map(type => type.name),
        memberKeys: children.map(type => type.id).join(','),
      });
      children.forEach(child => {
        result.push({
          key: child.id,
          text: child.name,
          category: child.parentId,
        });
      });
    }
  });
  return result;
};

export const dueDateFormatter = (dueDate, totalAnswered, totalQuestions) => {
  if (!dueDate) return '';

  const parsedDate = parseISO(dueDate);
  const today = new Date();
  const parsedDateString = format(parsedDate, 'MMM dd, yyyy');
  const isTotalAnswered = totalAnswered === totalQuestions;
  if (isBefore(parsedDate, today) && !isTotalAnswered)
    return <p className='tw:text-danger-dark '>{parsedDateString}</p>; //eslint-disable-line react/react-in-jsx-scope
  if (differenceInCalendarDays(parsedDate, today) <= 7 && !isTotalAnswered)
    return <p className='tw:text-warning-dark'>{parsedDateString}</p>; //eslint-disable-line react/react-in-jsx-scope

  return <p>{parsedDateString}</p>; //eslint-disable-line react/react-in-jsx-scope
};

export const populatePageNumbers = (totalCount, count) => {
  return new Array(Math.ceil(totalCount / count)).fill().map((x, i) => i);
};

export const isRetracedUserRole = role =>
  role in constants.RETRACED_ADMIN_ROLES_ENUM;

export const getRetracedRolesSelect = role =>
  constants.RETRACED_ADMIN_ROLES_ENUM.RETRACED_ADMIN === role
    ? constants.retracedRoles.selectArrayOptions
    : constants.retracedRoles.selectArrayOptions.filter(r => r.key === role);

export const checkDuplicatesInColumns = (arr, type, label, data) => {
  const errors = [];
  arr.forEach(item => {
    const strippedArr = data[item].map(i => i[type]);
    //Remove zeros from strippedArr
    const filteredValues = strippedArr.filter(i => i !== 0);
    if (filteredValues.length > 1) {
      const duplicates = filteredValues.filter(
        (item, index) => filteredValues.indexOf(item) !== index,
      );
      if (duplicates && duplicates.length > 0) {
        duplicates.forEach(duplicate => {
          const err = data[item].filter(i => i[type] === duplicate);
          err.forEach(e => {
            errors.push([
              {
                info: [
                  {
                    message: `Duplicate entry. This ${label} already exists for ${item}.`,
                    level: 'error',
                  },
                ],
              },
              e.index,
            ]);
          });
        });
      }
    }
  });

  return errors;
};

export const checkDuplicateProductWithLotColumns = productsLotPairs => {
  const errors = [];
  const seen = new Set();

  productsLotPairs.forEach(item => {
    const key = `${item.id}_${item.product}_${item.lotId}`;
    if (seen.has(key)) {
      errors.push([
        {
          info: [
            {
              message:
                'This product is already added with the same transaction number and lot. One transaction should have a unique product.',
              level: 'error',
            },
          ],
        },
        item.index,
      ]);
    } else {
      seen.add(key);
    }
  });

  return errors;
};

export const checkForSameDataInColumns = (arr, type, label, data) => {
  const errors = [];
  arr.forEach(item => {
    const strippedArr = data[item].map(i => i[type]);
    const filteredValues = strippedArr.filter(
      (value, index) => value[1] !== 0 && value[1] !== undefined,
    );
    if (filteredValues.length > 1) {
      const duplicates = strippedArr.filter(
        (item, index) => strippedArr.indexOf(item) !== index,
      );
      // Check if all items in duplicates are same
      if (
        duplicates &&
        duplicates.length > 0 &&
        duplicates.length !== strippedArr.length
      ) {
        const isSame = duplicates.every((val, i, arr) => val === arr[0]);
        if (isSame) {
          strippedArr.forEach((strippedArrItem, index) => {
            if (duplicates.indexOf(strippedArrItem) === -1) {
              errors.push([
                {
                  info: [
                    {
                      message: `${label} is not the same for the product ${item}.`,
                      level: 'error',
                    },
                  ],
                },
                data[item][index].index,
              ]);
            }
          });
        } else {
          strippedArr.forEach((strippedArrItem, index) => {
            errors.push([
              {
                info: [
                  {
                    message: `${label} is not the same for the product ${item}.`,
                    level: 'error',
                  },
                ],
              },
              data[item][index].index,
            ]);
          });
        }
      } else if (!duplicates || duplicates.length === 0) {
        strippedArr.forEach((strippedArrItem, index) => {
          errors.push([
            {
              info: [
                {
                  message: `${label} is not the same for the product ${item}.`,
                  level: 'error',
                },
              ],
            },
            data[item][index].index,
          ]);
        });
      }
    }
  });
  return errors;
};

export const checkForRequiredColumns = (type, label, data) => {
  const errors = [];
  const arr = Object.keys(data);

  arr.forEach(item => {
    const strippedArr = data[item].map(i => i[type]);
    const filteredValues = strippedArr.filter(
      (value, index) => value[1] !== 0 && value[1] !== undefined,
    );
    if (filteredValues.length === 0) {
      errors.push([
        {
          info: [
            {
              message: `Please enter a value for ${label} for the product ${item}.`,
              level: 'error',
            },
          ],
        },
        data[item][0].index,
      ]);
    }
  });

  return errors;
};

function parseDate(dateStr) {
  const dateFormats = [
    'dd/MM/yyyy',
    'MM/dd/yyyy',
    'yyyy-MM-dd',
    'dd-MM-yyyy',
    'MM-dd-yyyy',
    'yyyy/MM/dd',
    'dd.MM.yyyy',
    'MM.dd.yyyy',
    'yyyy.MM.dd',
  ];

  // Try parsing the date with each format until successful
  for (const dateFormat of dateFormats) {
    const parsedDate = parse(dateStr, dateFormat, new Date(), {
      strict: false,
    });
    if (!isNaN(parsedDate.getTime())) {
      return parsedDate;
    }
  }

  // If none of the formats match, return null
  return null;
}

export const formatDatesInDateData = (dateData, dateKey) => {
  const dateIds = Object.keys(dateData);
  //convert all date on DateData to date format of DD/MM/YYYY
  const formattedDateData = {};
  dateIds.forEach(id => {
    const result = dateData[id].map(item => {
      if (item[dateKey]) {
        if (!isNaN(new Date(item[dateKey]).getTime())) {
          const date = parseDate(item[dateKey]);
          if (date) {
            const formattedDate = format(date, 'dd-MM-yyyy');

            return {
              ...item,
              orderDate: formattedDate,
            };
          }
          return item;
        }
        return item;
      }
      return item;
    });
    formattedDateData[id] = result;
  });

  return formattedDateData;
};

export const cleanupDateValues = values =>
  values.map(([item, index]) => {
    const date = parseDate(item);

    //if not a valid date, return the item as is
    if (isNaN(date?.getTime())) {
      return [
        {
          value: item,
        },
        index,
      ];
    }
    const formattedDate = format(date, 'dd-MM-yyyy');

    //if the formatted date is the same as the original date, return the item as is
    if (formattedDate === item) {
      return [
        {
          value: item,
        },
        index,
      ];
    }

    return [
      {
        value: formattedDate,
        info: [
          {
            message:
              'We have automatically transformed the date into the correct format, ie. DD-MM-YYYY',
            level: 'info',
          },
        ],
      },
      index,
    ];
  });

export const correspondingDateFormat = date => {
  if (date) {
    const dateParts = String(date).split('-');
    return new Date(+dateParts[2], dateParts[1] - 1, +dateParts[0]);
  }
};

export const productFormatter = (product = {}, isAddOrder = false) => {
  const {
    name,
    code,
    purchaseOrderNumber,
    quantity,
    unit,
    productCategoryName,
  } = product;
  return `${name} / ${code ? `${code} /` : ''} ${
    !isAddOrder && purchaseOrderNumber ? `PO: ${purchaseOrderNumber}` : ''
  } - ${!isAddOrder && quantity ? quantity : ''} ${!isAddOrder && unit ? constants.units.mapping[unit] : ''} ${
    isAddOrder && productCategoryName ? productCategoryName : ''
  }`;
};

export const compositionFormatter = material => {
  if (!material) return '';

  return `${material.percentage}% ${material.name} - ${constants.countriesMapping[material.originCountry]}`;
};

export const getDefaultProductUnit = product => {
  if (product?.productCategoryQuantityUnits?.length) {
    const preferredUnits = [
      constants.units.enumMapping.PIECES,
      constants.units.enumMapping.METRE,
      constants.units.enumMapping.KILOGRAM,
    ];
    return (
      product.productCategoryQuantityUnits.find(p =>
        preferredUnits.includes(p),
      ) || null
    );
  }

  return constants.units.enumMapping.PIECES;
};

export const getDefaultStyleUnit = style => {
  if (style?.type?.quantityUnits?.length) {
    const preferredUnits = [
      constants.units.enumMapping.PIECES,
      constants.units.enumMapping.METRE,
      constants.units.enumMapping.KILOGRAM,
    ];
    return (
      style.type.quantityUnits.find(p => preferredUnits.includes(p)) || null
    );
  }

  return constants.units.enumMapping.PIECES;
};

export const getTransactionCompanies = transaction => {
  if (!transaction) return {};
  if (transaction.transactionType === 'OUTGOING')
    return {
      shipperCompany: transaction.company,
      receiverCompany: transaction.withCompany,
    };
  return {
    receiverCompany: transaction.company,
    shipperCompany: transaction.withCompany,
  };
};

export const buildExistingAndNewFacilityProcessesOptions = ({
  existingFacilityProcesses = [],
  facilityProcessesList = [],
}) => {
  if (existingFacilityProcesses.length === 0)
    return facilityProcessesList.map(facility => ({
      title: facility.title,
      category: facility.category,
      text: facility.text,
      key: facility.key,
      members: facility.members,
      isDisabled: facility.isDisabled,
      metadata: {
        isDisabled: facility.isDisabled,
      },
    }));

  const otherFacilityProcesses = facilityProcessesList.filter(
    fp => !existingFacilityProcesses.map(p => p.key).includes(fp.key),
  );

  const existingTitle = {
    isDisabled: true,
    key: 'EXISTING_FACILITY_PROCESSES',
    memberKeys: existingFacilityProcesses.map(e => e.id),
    members: existingFacilityProcesses.map(f => f.text),
    title: 'Existing facility processes',
  };

  const othersTitle = {
    isDisabled: true,
    key: 'OTHER_FACILITY_PROCESSES',
    memberKeys: '',
    members: [],
    title: 'Other facility processes',
  };

  return [
    existingTitle,
    ...existingFacilityProcesses,
    othersTitle,
    ...otherFacilityProcesses,
  ].map(facility => ({
    title: facility.title,
    category: existingFacilityProcesses?.find(ef => ef.key === facility.key)
      ? 'EXISTING_FACILITY_PROCESSES'
      : facility.category,
    text: facility.text,
    key: facility.key,
    members:
      facility.key !== 'EXISTING_FACILITY_PROCESSES'
        ? facility?.members?.filter(m => !existingTitle?.members?.includes(m))
        : facility.members,
    isDisabled: facility.isDisabled,
    metadata: {
      isDisabled: facility.isDisabled,
      isHeader: facility.isHeader,
    },
  }));
};

export const isToday = date => {
  const model = new Date(date);
  const today = new Date();

  return today.toDateString() === model.toDateString();
};

export const getPredefinedSelectOptions = type => {
  switch (type) {
    case constants.preDefinedSelectTypes.enumMapping.COUNTRIES:
      return constants.countriesList.selectArrayOptions;
    case constants.preDefinedSelectTypes.enumMapping.FACILITY_PROCESSES:
      return useFacilityProcessesList().map(facility => ({
        ...facility,
        metadata: {
          isDisabled: facility.isDisabled,
        },
      }));
    case constants.preDefinedSelectTypes.enumMapping.LANGUAGES:
      return constants.countriesLanguage.selectArrayOptions;
    default:
      return [];
  }
};

export const userNameFormatter = user =>
  user ? `${user.firstName} ${user.lastName}` : '';

/**
 * Display formatter for user information using network data lookup
 * @param {string} userId - The user ID to lookup
 * @param {Array} networkUsersList - Array of users from network data
 * @returns {string} Formatted user display name
 */
export const getUserDisplayName = (userId, networkUsersList = []) => {
  // If we have a user ID, try to find the user in our network list
  if (userId) {
    const user = networkUsersList?.find(u => u.id === userId);
    if (user) {
      // Return full name or fallback to firstName/lastName or email
      return (
        user.fullName ||
        (user.firstName && user.lastName
          ? `${user.firstName} ${user.lastName}`
          : '') ||
        user.email ||
        user.name
      );
    }

    // Fallback to ID if not found in network
    return userId;
  }

  return '';
};

/**
 * Display formatter for company information using network data lookup
 * @param {string} companyId - The company ID to lookup
 * @param {Array} networkCompaniesList - Array of companies from network data
 * @returns {string} Company display name
 */
export const getCompanyDisplayName = (companyId, networkCompaniesList = []) => {
  // If we have a company ID, try to find the company in our network list
  if (companyId) {
    const company = networkCompaniesList?.find(comp => comp.id === companyId);
    if (company) return company.displayName || company.name;

    // Fallback to ID if not found in network
    return companyId;
  }

  return '';
};

/**
 * Display formatter for impact area information
 * @param {string} impactAreaId - The impact area ID to lookup
 * @param {Array} impactAreasList - Array of impact areas
 * @returns {string} Formatted impact area display (hierarchyId: description)
 */
export const getImpactAreaDisplayName = (
  impactAreaId,
  impactAreasList = [],
) => {
  if (!impactAreaId) return '';

  const impactArea = impactAreasList?.find(area => area.id === impactAreaId);
  if (!impactArea) return impactAreaId; // Fallback to ID if not found

  // Return hierarchyId : description format like in the old CAPA system
  return impactArea.hierarchyId && impactArea.description
    ? `${impactArea.hierarchyId}: ${impactArea.description}`
    : impactArea.description || impactArea.hierarchyId || impactAreaId;
};

export const filterObjectsByIds = (arrayOfObjects, arrayOfIds) => {
  return Array.from(
    arrayOfObjects.filter(object => arrayOfIds.includes(object.id)),
  );
};

export const removeDuplicatesInArrayById = arr =>
  Object.values(arr.reduce((cu, c) => ({ ...cu, [c.id]: c }), {}));

export const buildSourceAuditList = ({ existingSourceAuditsList = [] }) => {
  const sources = existingSourceAuditsList.map(source => ({
    id: source.id,
    key: source.id,
    name: source.auditTypeName,
    auditTypeName: source.auditTypeName,
    text: `${source.name !== '' ? source.name : source.auditTypeName} | ${
      source.referenceNumber
        ? 'Reference Number: ' + source.referenceNumber
        : 'Audit date: ' + new Date(source.auditDate).toDateString()
    }`,
    auditDate: source.auditDate,
    referenceNumber: source.referenceNumber,
    permission: source.permission,
    privacyStatus: {
      status: source.privacyStatus?.status,
    },
    companyId: source.companyId,
  }));
  return sources;
};

export const getQuestionAnswerStatus = question => {
  let status = 'Not submitted yet';
  let statusClassName = 'missing';
  let color = 'gray-lighter';
  if (question?.status) {
    status =
      constants.assessmentFormQuestionStatuses.enumMapping[
        question.status.status
      ];
    switch (status) {
      case constants.assessmentFormQuestionStatuses.enumMapping.APPROVED:
        statusClassName = 'approved';
        color = 'success';
        break;
      case constants.assessmentFormQuestionStatuses.enumMapping.DENIED:
        statusClassName = 'denied';
        color = 'danger';
        break;
      case constants.assessmentFormQuestionStatuses.enumMapping.INREVIEW:
        statusClassName = 'inreview';
        color = 'warning';
        break;
      default:
        color = 'custom-gray';
        break;
    }
  }

  return {
    status,
    color,
    statusClassName,
  };
};

export const convertToLowerCaseAndRemoveSpaces = text => {
  return text.toLowerCase().replace(/\s+/g, '');
};

export const ungroupArray = arr => {
  const array = [];

  const ungroup = (fields, category = null) => {
    for (const field of fields) {
      if (field.category) {
        ungroup(field.dataPoints, {
          name: field.category,
          id: field.id,
          templateId: field.templateId,
        });
      } else {
        if (category) {
          field.category = category.name;
          field.categoryId = category.id;
          field.templateId = category.templateId;
        }
        array.push(field);
      }
    }
  };

  ungroup(structuredClone(arr));

  return array;
};

export const groupArrayByCategory = arr => {
  const groupArray = [];
  for (const field of arr) {
    if (field.category) {
      const foundCategoryIndex = groupArray.findIndex(
        foundField => foundField.category === field.category,
      );

      if (foundCategoryIndex > -1) {
        groupArray[foundCategoryIndex].dataPoints.push(field);
      } else {
        groupArray.push({
          id: field.categoryId,
          category: field.category,
          templateId: field.templateId,
          dataPoints: [field],
          sectionId: field.sectionId,
        });
      }
    } else {
      groupArray.push(field);
    }
  }

  return groupArray;
};

export const isValidNumberValue = value => {
  if (typeof value === 'number' && !isNaN(value)) {
    return true;
  }

  // Check if the value is a string that can be converted to a valid number
  if (
    typeof value === 'string' &&
    value.trim() !== '' &&
    !isNaN(Number(value))
  ) {
    return true;
  }
};

export const checkFieldAnswer = field => {
  if (
    field.fieldTypeId === constants.companyDataFieldsTypes.enumMapping.TABLE
  ) {
    const d = field.rows[0].columns.map(column => {
      return shouldValueBeNumber(column.fieldTypeId)
        ? isValidNumberValue(column?.value?.value)
          ? column?.value?.value
          : null
        : column.value;
    });

    return d.every(v => (v?.value ? v?.value?.length > 0 : v != null));
  }
  if (
    field.fieldTypeId === constants.companyDataFieldsTypes.enumMapping.CHECK_BOX
  ) {
    return field.value?.some(val => !isNullOrUndefined(val));
  }
  if (
    field.fieldTypeId === constants.companyDataFieldsTypes.enumMapping.NUMBER
  ) {
    return isValidNumberValue(field.value);
  }
  if (shouldValueBeNumber(field.fieldTypeId)) {
    return isValidNumberValue(field.value?.value);
  }

  return field.value ? true : false;
};

export const buildImpactAreaTree = ({
  impactAreas,
  parentId = null,
  fieldsData = [],
}) => {
  return impactAreas
    .filter(impactArea => impactArea.parentId === parentId)
    .map(impactArea => {
      const fieldDataPoints = fieldsData.find(
        f => f.templateId === impactArea.id,
      );
      const children = buildImpactAreaTree({
        impactAreas,
        parentId: impactArea.id,
        fieldsData,
      });

      // temporary FE statistics, endpoint for statistics will be provided soon
      let totalDataPointsCount = fieldDataPoints?.dataPoints?.length || 0;
      let totalAnsweredDataPointsCount = checkCategoryAnswers(
        fieldDataPoints?.dataPoints || [],
      );

      if (impactArea.isParent) {
        totalAnsweredDataPointsCount = children.reduce(
          (n, { statistics }) =>
            n +
            (statistics?.totalAnsweredDataPointsCount
              ? Number(statistics?.totalAnsweredDataPointsCount)
              : 0),
          0,
        );

        totalDataPointsCount = children.reduce(
          (n, { statistics }) =>
            n +
            (statistics?.totalDataPointsCount
              ? Number(statistics?.totalDataPointsCount)
              : 0),
          0,
        );
      }

      // Only show the impact area with data points
      const isHidden =
        (!impactArea.isParent && totalDataPointsCount === 0) ||
        (impactArea.isParent &&
          children.filter(child => !child.isHidden).length === 0);

      return {
        ...impactArea,
        isHidden,
        name: impactArea.name,
        children,
        statistics: {
          totalDataPointsCount,
          totalAnsweredDataPointsCount,
        },
      };
    });
};

export const checkCategoryAnswers = dataPoints => {
  let totalAnswered = 0;
  for (const dataPoint of dataPoints) {
    if (checkFieldAnswer(dataPoint)) {
      totalAnswered += 1;
    }
  }
  return totalAnswered;
};

export const snakeToCamelCase = s => {
  return s.replace(/(_\w)/g, match => match[1].toUpperCase());
};

export const camelCaseToWords = s => {
  const result = s.replace(/([A-Z])/g, ' $1');
  return result.charAt(0).toUpperCase() + result.slice(1);
};

export const customStringToSnakeCase = c => {
  return c.replace(/\s+/g, '_').toLowerCase();
};

export const categoryTextToDisplay = c => {
  const stringWithSpaces = c.replace(/_/g, ' ');
  const formattedString =
    stringWithSpaces.charAt(0).toUpperCase() +
    stringWithSpaces.slice(1).toLowerCase();
  return formattedString;
};

export const isValidJson = data => {
  try {
    JSON.parse(data);
    return true;
  } catch {
    return false;
  }
};

export const buildDynamicComponents = (categories, selectOptions) => {
  //assuming meta has information of placement in frontend like category and table_id or need to hard code the categories
  const fieldsToSkipForAddress = [
    'country',
    'city',
    'zip_code',
    'address_line_1',
    'address_line_2',
  ];
  const components = [];
  let count = 0;
  const addressInfo = {};

  const processProperties = (componentKey, component) => {
    if (!component.fieldTypeId) return null;

    if (fieldsToSkipForAddress.includes(componentKey)) return null;

    const meta = component.meta; //meta is not strigified anymore
    if (!isNullOrUndefined(component.isVisible) && !component.isVisible)
      return null;

    let properties = {
      dataPoint: component,
      id: count++,
      key: component.id, //key is for BE
      type: component.fieldTypeId,
      fieldTypeId: component.fieldTypeId,
      name: component.name,
      value: component.value,
      placeholder: component.name,
      meta,
      schema: component.schema,
      sectionId: component.sectionId,
      isEditable: component.isEditable,
      sortingId: component.sortingId,
      verificationMeta: component.verificationMeta,
      currentValue: component.value,
    };
    if (component.unitOptions)
      properties['unitOptions'] = component.unitOptions;

    if (meta && Object.keys(meta).length > 0) {
      if (meta.unit) component.unit = meta.unit;
      if (meta.unitText) component.unitText = meta.unitText;
      if (meta.unitOptions) component.unitOptions = meta.unitOptions;
    }

    const componentOptions = Array.isArray(component.options)
      ? component.options.map(o => ({
          ...o,
          key: o.key || o.id,
          text: o.text || o.name,
        }))
      : [];

    switch (component.fieldTypeId) {
      case constants.companyDataFieldsTypes.enumMapping.DATE: {
        if (component.value)
          properties = {
            ...properties,
            value: new Date(component.value),
          };
        break;
      }
      case constants.companyDataFieldsTypes.enumMapping.SELECT_YEAR: {
        if (component.year)
          properties = {
            ...properties,
            value: component.year,
          };
        break;
      }
      case constants.companyDataFieldsTypes.enumMapping.SELECT: {
        const camelCaseKey = snakeToCamelCase(component.id);
        if (selectOptions[camelCaseKey]?.length > 0) {
          properties = {
            ...properties,
            options: selectOptions[camelCaseKey],
            value: component.value
              ? selectOptions[camelCaseKey].find(c => c.key === component.value)
                ? {
                    text: selectOptions[camelCaseKey].find(
                      c => c.key === component.value,
                    ).text,
                    value: component.value,
                  }
                : null
              : null,
          };
        } else {
          properties = {
            ...properties,
            options: componentOptions,
            value: component.value
              ? componentOptions?.find(c => c.key === component.value)
                ? {
                    text: componentOptions?.find(c => c.key === component.value)
                      .text,
                    value: component.value,
                  }
                : null
              : null,
          };
        }
        break;
      }
      case constants.companyDataFieldsTypes.enumMapping.MULTI_SELECT: {
        const camelCaseKey = snakeToCamelCase(component.id);
        if (selectOptions[camelCaseKey]?.length > 0) {
          properties = {
            ...properties,
            options: selectOptions[camelCaseKey],
            value:
              Array.isArray(component.value) && component.value.length > 0
                ? component.value
                    ?.map(v => ({
                      ...v,
                      key: selectOptions[camelCaseKey].find(
                        k => k.key === v.id || k.key === v,
                      )?.key,
                      text: selectOptions[camelCaseKey].find(
                        k => k.key === v.id || k.key === v,
                      )?.text,
                    }))
                    .filter(v => v.key !== undefined)
                : [],
          };
        } else {
          properties = {
            ...properties,
            options: componentOptions,
            value:
              Array.isArray(component.value) && component.value.length > 0
                ? componentOptions.value?.map(v => ({
                    ...v,
                    //multiselect has answer both mapped with id and just values
                    key: componentOptions.find(
                      k => k.key === v.id || k.key === v,
                    )?.key,
                    text: componentOptions.find(
                      k => k.key === v.id || k.key === v,
                    )?.text,
                  }))
                : [],
          };
        }
        break;
      }

      case constants.companyDataFieldsTypes.enumMapping.ADDRESS: {
        //address is geoJson//
        properties = {
          ...properties,
          value: {
            ...addressInfo,
            //geojson.value are strigified after collocation
            geojson: isValidJson(component.value)
              ? component.value
              : JSON.stringify(component.value),
          },
        };
        break;
      }

      case constants.companyDataFieldsTypes.enumMapping.NUMBER_UOM: {
        if (component.unit) {
          properties = {
            ...properties,
            value: {
              uom: {
                ...constants.units.selectArrayOptions.find(
                  u => u.key === component.unit,
                ),
                label: component.unitText,
              },
              value: component.value,
              unitText: component.unitText
                ? component.unitText
                : constants.units.selectArrayOptions.find(
                    u => u.key === component.unit,
                  )?.label,
            },
          };
        } else {
          properties = { ...properties, value: '' };
        }
        break;
      }
      case constants.companyDataFieldsTypes.enumMapping.NUMBER_DYNAMIC_UOM: {
        if (component.unit) {
          properties = {
            ...properties,
            value: {
              uom: {
                ...constants.units.selectArrayOptions.find(
                  u => u.key === component.unit,
                ),
                label: component.unitText,
              },

              value: component.value,
            },
          };
        } else {
          properties = { ...properties, value: '' };
        }
        break;
      }
      case constants.companyDataFieldsTypes.enumMapping
        .NUMBER_DYNAMIC_CURRENCY: {
        if (component.value && component.unit) {
          properties = {
            ...properties,
            value: {
              uom: {
                label: constants.countriesCurrencyDropdownSelectOptions.find(
                  u => u.key === component.unit,
                )?.label,
                key: component.unit,
              },
              value: component.value ? component.value.toString() : '',
            },
          };
        } else {
          properties = { ...properties, value: '' };
        }
        break;
      }
      case constants.companyDataFieldsTypes.enumMapping
        .NUMBER_DYNAMIC_CURRENCY_WITH_YEAR: {
        if (component.value && component.unit) {
          properties = {
            ...properties,
            value: {
              unit: {
                label: constants.countriesCurrencyDropdownSelectOptions.find(
                  u => u.key === component.unit,
                )?.label,
                key: component.unit,
              },
              value: component.value ? component.value.toString() : '',
              year: component.year ? component.year : null,
            },
          };
        } else {
          properties = { ...properties, value: '' };
        }
        break;
      }
      case constants.companyDataFieldsTypes.enumMapping
        .NUMBER_DYNAMIC_UOM_WITH_YEAR: {
        if (component.value && component.unit) {
          properties = {
            ...properties,
            value: {
              unit: {
                label: constants.units.selectArrayOptions.find(
                  u => u.key === component.unit,
                )?.label,
                key: component.unit,
              },
              value: component.value ? component.value.toString() : '',
              year: component.year ? component.year : null,
            },
          };
        } else {
          properties = { ...properties, value: '' };
        }
        break;
      }
      case constants.companyDataFieldsTypes.enumMapping.TABLE:
        {
          const tableColumnsMeta =
            typeof component.meta === 'string' && isValidJson(component.meta)
              ? JSON.parse(component.meta)
              : typeof component.meta === 'object'
                ? component.meta
                : null;

          if (!tableColumnsMeta) return null;
          if (tableColumnsMeta.columns?.length === 0) return null;
          const rows = [];
          const rowValues = Array.isArray(component.value)
            ? component.value
            : [component.value];

          if (rowValues.length === 0) {
            const columns = [];
            for (const [index, column] of Object.entries(meta.columns)) {
              const col = processProperties(index, {
                ...column,
                fieldTypeId: column.type,
                id: column.id,
                name: column.name ? column.name : column.text,
                value: rowValues
                  ? rowValues[snakeToCamelCase(column.id)]
                  : null,
                unit:
                  rowValues && rowValues['unit']
                    ? rowValues['unit']
                    : column.unit
                      ? column.unit
                      : undefined,
                year: rowValues && rowValues['year'] ? rowValues['year'] : '',
                date: rowValues && rowValues['date'] ? rowValues['year'] : null,
              });
              columns.push(col);
            }
            rows.push({ columns: columns });
          } else {
            for (const value of rowValues) {
              const columns = [];
              for (const [index, column] of Object.entries(
                tableColumnsMeta.columns,
              )) {
                const col = processProperties(index, {
                  ...column,
                  fieldTypeId: column.type,
                  id: column.id,
                  name: column.name ? column.name : column.text,
                  value: value ? value[snakeToCamelCase(column.id)] : null,
                  unit:
                    value && value['unit']
                      ? value['unit']
                      : column.unit
                        ? column.unit
                        : undefined,
                  year: value && value['year'] ? value['year'] : '',
                  date: value && value['date'] ? value['date'] : null,
                });
                columns.push(col);
              }
              rows.push({ columns: columns });
            }
          }

          properties = {
            ...properties,
            isMultipleRow: meta.isMultipleRow ?? false,
            rows: rows,
          };
        }
        break;
      case constants.companyDataFieldsTypes.enumMapping.BOOLEAN:
        properties = {
          ...properties,
          value: component.value === 'YES' ? true : false,
        };
        break;
      case constants.companyDataFieldsTypes.enumMapping.YES_NO:
        properties = {
          ...properties,
          options:
            componentOptions.length > 0
              ? componentOptions
              : [
                  { key: 'YES', text: 'Yes' },
                  { key: 'NO', text: 'No' },
                ],
          value: component.value,
        };
        break;

      case constants.companyDataFieldsTypes.enumMapping.CHECK_BOX:
        properties = {
          ...properties,
          options: componentOptions || [],
          value: componentOptions.map(option => {
            const actualValue = component.value?.find(
              v => v.key === option.key,
            );

            if (actualValue)
              return {
                isChecked: Boolean(actualValue?.isChecked),
                key: option.key,
                followUp: actualValue?.followUp || null,
              };

            return null;
          }),
        };
        break;

      case constants.companyDataFieldsTypes.enumMapping.SELECT_WITH_FOLLOW_UP: {
        const camelCaseKey = snakeToCamelCase(component.id);
        if (selectOptions[camelCaseKey]?.length > 0) {
          properties = {
            ...properties,
            options: selectOptions[camelCaseKey],
            value: component.value?.value
              ? selectOptions[camelCaseKey].find(c => c.key === component.value)
                ? {
                    text: selectOptions[camelCaseKey].find(
                      c => c.key === component.value.value,
                    ).text,
                    value: component.value.value,
                    followUp: component.value.followUp || null,
                  }
                : null
              : null,
          };
        } else {
          properties = {
            ...properties,
            options: componentOptions,
            value: component.value?.value
              ? componentOptions?.find(c => c.key === component.value.value)
                ? {
                    text: componentOptions?.find(
                      c => c.key === component.value.value,
                    ).text,
                    value: component.value.value,
                    followUp: component.value.followUp || null,
                  }
                : null
              : null,
          };
        }
        break;
      }
    }

    return properties;
  };

  //setting address field
  const customSettingAddressField = (key, component) => {
    const keyCamelCase = snakeToCamelCase(key);
    addressInfo[keyCamelCase] = component.value;
  };

  Object.keys(categories).forEach(category => {
    const columns = categories[category];
    const categoryComponent = {
      isOpen: false,
      category: category,
      templateId: columns.templateId,
      dataPoints: [],
    };

    const addressKeys = Object.keys(columns).filter(k =>
      fieldsToSkipForAddress.includes(k),
    );
    addressKeys.forEach(key => {
      customSettingAddressField(key, columns[key]);
    });

    Object.keys(columns).forEach(key => {
      const property = processProperties(key, columns[key]);
      if (property) categoryComponent.dataPoints.push(property);
    });
    if (categoryComponent.dataPoints.length > 0)
      components.push(categoryComponent);
  });

  const sortedComponents = components.map(component => ({
    ...component,
    dataPoints: component.dataPoints.sort((a, b) => {
      if (isNullOrUndefined(a.sortingId)) return 1;
      if (isNullOrUndefined(b.sortingId)) return -1;
      return a.sortingId - b.sortingId;
    }),
  }));
  return sortedComponents;
};

export const getRequiredFields = categories =>
  categories.map(category => ({
    category: category.category,
    fields: category.dataPoints
      .filter(dataPoints => dataPoints.meta?.required === true)
      .map(dataPoint => ({ key: dataPoint.key, name: dataPoint.name })),
  }));

export const buildPayload = (data, fieldArrayName) => {
  // country_name is hidden on FE, so we can't modify it manually. Need to remove and patch the value later in geojson.
  if (fieldArrayName === 'general-info') {
    data[fieldArrayName] = [
      {
        ...data[fieldArrayName][0],
        dataPoints: data[fieldArrayName][0].dataPoints.filter(
          item => item.key !== 'country_name',
        ),
      },
    ];
  }
  const fieldsToSkipForAddress = [
    'country_name',
    'country',
    'city',
    'zip_code',
    'address_line_1',
    'address_line_2',
    'geojson',
  ];

  const customProcessingOfAddressType = dataPoint => {
    const addressFields = fieldsToSkipForAddress.map(f => ({
      [snakeToCamelCase(f)]: f,
    }));
    let payload = {};
    for (const item of addressFields) {
      const [key] = Object.keys(item);
      let value = dataPoint.value[key];

      if (isNullOrUndefined(value)) {
        value = null;
      }
      if (key === 'geojson') {
        if (isValidJson(value)) {
          const geoJson = JSON.parse(value);
          value = geoJson;
        } else value = null;
      }
      if (item[key] === 'zip_code') {
        value = value ? value : null;
      }

      if (item[key] === 'country_name' && value == null) {
        const countryKey = addressFields.find(field => field.country)?.country;
        if (countryKey) {
          value = constants.countriesMapping[dataPoint.value[countryKey]];
        }
      }
      payload = { ...payload, [item[key]]: { value: value } };
    }
    return payload;
  };

  const processPropertyBuildPayload = dataPoint => {
    let dataPointAnswer = {};
    let isValueChanged = false;

    const compare = (a, b) => !isEqual(a, b);

    switch (dataPoint.type) {
      case constants.companyDataFieldsTypes.enumMapping.SELECT: {
        let value = null;
        if (!isNullOrUndefined(dataPoint.value)) {
          value = dataPoint.value?.value
            ? dataPoint.value?.value
            : dataPoint.value?.key;
        }
        dataPointAnswer = { value };
        isValueChanged = compare(value, dataPoint.currentValue);
        break;
      }

      case constants.companyDataFieldsTypes.enumMapping.MULTI_SELECT: {
        let value = null;
        if (
          !isNullOrUndefined(dataPoint.value) &&
          Array.isArray(dataPoint.value)
        ) {
          const schemaType = dataPoint.schema?.properties?.value?.items?.type;
          const schemaHasNoIds = schemaType === 'string';
          value = dataPoint.value.map(v =>
            schemaHasNoIds ? v.key : { id: v.id ? v.id : v.key },
          );
        }
        dataPointAnswer = { value };
        isValueChanged = compare(value, dataPoint.currentValue);
        break;
      }

      case constants.companyDataFieldsTypes.enumMapping.ADDRESS:
        return {
          ...customProcessingOfAddressType(dataPoint),
        };

      case constants.companyDataFieldsTypes.enumMapping.NUMBER: {
        const value = !isNaN(dataPoint.value)
          ? Number.parseFloat(dataPoint.value)
          : null;
        dataPointAnswer = { value };
        isValueChanged = compare(value, dataPoint.currentValue);
        break;
      }

      case constants.companyDataFieldsTypes.enumMapping.NUMBER_UOM: {
        const value = isValidNumberValue(dataPoint.value?.value)
          ? Number.parseFloat(dataPoint.value?.value)
          : null;
        const unit = dataPoint.value?.uom?.key;
        dataPointAnswer = dataPoint.value ? { value, unit } : { value: null };
        isValueChanged = compare({ value, unit }, dataPoint.currentValue);
        break;
      }

      case constants.companyDataFieldsTypes.enumMapping.NUMBER_DYNAMIC_UOM:
      case constants.companyDataFieldsTypes.enumMapping
        .NUMBER_DYNAMIC_CURRENCY: {
        const value = isValidNumberValue(dataPoint.value?.value)
          ? Number.parseFloat(dataPoint.value?.value)
          : null;
        const unit = dataPoint.value?.uom?.key;
        dataPointAnswer = dataPoint.value?.uom
          ? { value, unit }
          : { value: null };
        isValueChanged = compare({ value, unit }, dataPoint.currentValue);
        break;
      }

      case constants.companyDataFieldsTypes.enumMapping
        .NUMBER_DYNAMIC_CURRENCY_WITH_YEAR:
      case constants.companyDataFieldsTypes.enumMapping
        .NUMBER_DYNAMIC_UOM_WITH_YEAR: {
        const value = isValidNumberValue(dataPoint.value?.value)
          ? Number.parseFloat(dataPoint.value?.value)
          : null;
        const unit = dataPoint.value?.unit?.key;
        const year = dataPoint.value?.year ?? null;
        dataPointAnswer = dataPoint.value?.unit
          ? { value, unit, year }
          : { value: null };
        isValueChanged = compare({ value, unit, year }, dataPoint.currentValue);
        break;
      }

      case constants.companyDataFieldsTypes.enumMapping.BOOLEAN: {
        const value = Boolean(dataPoint.value);
        dataPointAnswer = { value };
        isValueChanged = compare(value, dataPoint.currentValue);
        break;
      }

      case constants.companyDataFieldsTypes.enumMapping.YES_NO: {
        const value = dataPoint.value === 'YES' ? 'YES' : 'NO';
        dataPointAnswer = { value };
        isValueChanged = compare(value, dataPoint.currentValue);
        break;
      }

      case constants.companyDataFieldsTypes.enumMapping.VIDEO_UPLOAD: {
        const value = dataPoint.value?.id ? dataPoint.value : {};
        dataPointAnswer = { value };
        isValueChanged = compare(value, dataPoint.currentValue);
        break;
      }

      case constants.companyDataFieldsTypes.enumMapping.SELECT_YEAR: {
        const value = Number(dataPoint.value);
        dataPointAnswer = { value };
        isValueChanged = compare(value, dataPoint.currentValue);
        break;
      }

      case constants.companyDataFieldsTypes.enumMapping.TABLE: {
        const rowValues = [];

        if (dataPoint.rows?.length > 0) {
          for (const row of dataPoint.rows) {
            let columns = {};
            for (const column of row.columns) {
              const col = processPropertyBuildPayload(column);
              if (!column.key) continue;

              if (
                shouldValueBeNumber(column.fieldTypeId) &&
                !isValidNumberValue(col[column.key]?.value)
              )
                continue;

              switch (column.type) {
                case constants.companyDataFieldsTypes.enumMapping.NUMBER_UOM:
                case constants.companyDataFieldsTypes.enumMapping
                  .NUMBER_DYNAMIC_UOM:
                case constants.companyDataFieldsTypes.enumMapping
                  .NUMBER_DYNAMIC_CURRENCY:
                  columns = { ...columns, unit: column.value?.uom?.key };
                  break;
                case constants.companyDataFieldsTypes.enumMapping
                  .NUMBER_DYNAMIC_CURRENCY_WITH_YEAR:
                case constants.companyDataFieldsTypes.enumMapping
                  .NUMBER_DYNAMIC_UOM_WITH_YEAR:
                  columns = {
                    ...columns,
                    unit: column.value?.uom?.key,
                    year: Number(columns.value?.year),
                  };
                  break;
              }

              columns = {
                ...columns,
                [snakeToCamelCase(column.key)]: col[column.key]?.value,
              };
            }

            if (Object.keys(columns).length > 0) rowValues.push(columns);
          }
        }

        const value =
          rowValues.length === 0
            ? null
            : dataPoint.isMultipleRow === true
              ? rowValues
              : rowValues[0];

        dataPointAnswer = { value };
        isValueChanged = compare(value, dataPoint.currentValue);
        break;
      }

      case constants.companyDataFieldsTypes.enumMapping.CHECK_BOX: {
        let value = null;
        if (dataPoint.value?.length > 0) {
          value = dataPoint.value
            .filter(f => f?.key)
            .map(value => ({
              ...value,
              followUp: value?.followUp?.value ? value.followUp : null,
            }));
        }
        dataPointAnswer = { value };
        isValueChanged = compare(value, dataPoint.currentValue);
        break;
      }

      case constants.companyDataFieldsTypes.enumMapping.SELECT_WITH_FOLLOW_UP: {
        const value = !isNullOrUndefined(dataPoint.value)
          ? dataPoint.value
          : null;
        dataPointAnswer = { value };
        isValueChanged = compare(value, dataPoint.currentValue);
        break;
      }

      default: {
        const value = dataPoint.value;
        dataPointAnswer = { value };
        isValueChanged = compare(value, dataPoint.currentValue);
        break;
      }
    }

    return dataPoint.verificationMeta && !isValueChanged
      ? {
          [dataPoint.key]: {
            ...dataPointAnswer,
            verificationMeta: dataPoint.verificationMeta,
          },
        }
      : { [dataPoint.key]: dataPointAnswer };
  };

  let body = {};
  for (const item of data[fieldArrayName]) {
    const templateId = item.category;
    const dataPoints = item.dataPoints;
    let answer = {};
    for (const dataPoint of dataPoints) {
      answer = { ...answer, ...processPropertyBuildPayload(dataPoint) };
    }
    body = { ...body, [templateId]: answer };
  }
  return body;
};
export const transformQuestionForBuilder = (question, sectionId) => {
  const isCustom = question.isCustom === true || question.isCustom === 'YES';

  const checkboxOrMultipleChoiceTypes = [
    constants.supplierQuestionnaireAnswerTypes.enumMapping.CHECK_BOX,
    constants.supplierQuestionnaireAnswerTypes.enumMapping.CHECK_BOXES,
    constants.supplierQuestionnaireAnswerTypes.enumMapping.MULTIPLE_CHOICE,
  ];

  const isCheckboxOrMultipleChoice = checkboxOrMultipleChoiceTypes.includes(
    question?.type,
  );
  return {
    ...question,
    sectionId: question?.sectionId || sectionId,
    questionId: question.id,
    isNew: false,
    questionTitle: question.title,
    answerMandatory: question?.isCompulsory > 0 ? true : false,
    attachFiles: question?.attachmentFileId ? true : false,
    isCustom,
    retracedDatapointId: question?.retracedDatapointId,
    isOptionMapped: question?.retracedDatapointId ? true : false,
    files: question?.attachmentFileId
      ? [
          {
            id: question?.attachmentFileId,
            name: question?.attachmentFileName,
            fileType: question?.attachmentFileType,
            contentType: question?.attachmentFileContentType,
            uploadUrl: question?.attachmentFileUrl,
            url: question?.attachmentFileUrl,
            originalUrl: question?.attachmentFileOriginalUrl,
          },
        ]
      : null,
    answerType: {
      key: question?.type,
      text: {
        ...constants.adminFormDataMappingElements.raw,
        ...constants.supplierQuestionnaireAnswerTypes.raw,
        ...constants.companyDataFieldsTypes.raw,
      }[question?.type],
    },
    options: question.options?.map(option => {
      if (isCustom) {
        if (isCheckboxOrMultipleChoice) {
          return {
            ...option,
            text: option.text || option.value,
            key: option.key || option.optionId,
            optionId: option.optionId,
            meta: { type: option.linkingAsset, category: option?.category },
          };
        }
        return {
          ...option,
          text: option.text || option.value,
          key: option.key || option.optionId,
          optionId: option.optionId,
          title: option.title || option.value,
          meta: { type: option.linkingAsset, category: option?.category },
        };
      }
      return {
        ...option,
        key: option.key || option.optionId,
        optionId: option.optionId,
        meta: { type: option.linkingAsset, category: option?.category },
      };
    }),
  };
};

const shouldValueBeNumber = fieldType =>
  [
    constants.companyDataFieldsTypes.enumMapping.NUMBER_UOM,
    constants.companyDataFieldsTypes.enumMapping.NUMBER_DYNAMIC_UOM,
    constants.companyDataFieldsTypes.enumMapping.NUMBER_DYNAMIC_CURRENCY,
    constants.companyDataFieldsTypes.enumMapping.NUMBER_DYNAMIC_UOM_WITH_YEAR,
    constants.companyDataFieldsTypes.enumMapping
      .NUMBER_DYNAMIC_CURRENCY_WITH_YEAR,
  ].includes(fieldType);

export const transformQuestionsToPayload = (question, questionState) => {
  const isCustom = question.isCustom === true || question.isCustom === 'YES';
  const options = questionState
    .find(q => q.questionId === question.questionId)
    ?.options.map(option => {
      const opt = {
        ...option,
        key: option.key || option.optionId,
        value: option.value || null,
      };
      if (isCustom) {
        opt.text = option.text || option.title || option.value;
      } else if (!option.text) delete opt.text;
      delete opt.meta;
      return opt;
    });

  const newQuestion = {
    id: question.questionId,
    title: question?.questionTitle,
    sectionId: question?.sectionId,
    questionTypeId: question?.answerType?.key,
    description: question?.description || null,
    meta: {
      options,
      isCompulsory: question?.answerMandatory ? 1 : 0,
      unit: questionState.find(q => q.questionId === question.questionId)?.unit,
    },
    isCustom:
      question.isCustom === true || question.isCustom === 'YES' ? true : false,
    retracedDatapointId:
      typeof question.retracedDatapointId === 'object'
        ? question.retracedDatapointId?.key
        : question.retracedDatapointId || null,
  };

  if (question.attachFiles) {
    newQuestion.meta = {
      ...newQuestion.meta,
      attachmentFileContentType: question?.files[0]?.contentType,
      attachmentFileId: question?.files[0]?.id,
      attachmentFileName: question?.files[0]?.name,
      attachmentFileType: question?.files[0]?.fileType,
      attachmentFileUrl: question?.files[0]?.uploadUrl,
      attachmentFileOriginalUrl: question?.files[0]?.originalUrl,
    };
  }

  return newQuestion;
};

export const transformToPayload = (questionsArray, sections) => {
  const sectionIds = [...new Set(questionsArray.map(q => q.sectionId))];
  const returnQuestions = questionsArray.map((q, index) => ({
    ...q,
    sortOrder: index + 1,
  }));
  const returnSections = [];
  if (
    sectionIds.length === 1 &&
    sections[0].sectionName === constants.DEFAULT_SECTION_NAME.SECTION_ONE
  ) {
    //default section dont send
    return {
      sections: returnSections,
      questions: returnQuestions.map(q => ({ ...q, sectionId: null })),
    };
  }
  for (const [index, sectionId] of sectionIds.entries()) {
    const sectionInfo = sections.find(s => s.sectionId === sectionId);
    returnSections.push({
      id: sectionId,
      name: sectionInfo.sectionName,
      description: sectionInfo.description || '',
      sortOrder: index + 1,
    });
  }

  return { sections: returnSections, questions: returnQuestions };
};

export const normalizeQuestionnaireAnswer = answer => {
  if (answer === true || answer?.value === true) {
    return 'Yes';
  }
  if (answer === false || answer?.value === false) {
    return 'No';
  }
  if (answer?.value !== undefined) {
    return answer.value;
  }
  if (answer?.linkedAsset) {
    return answer.linkedAsset;
  }
  return answer;
};
export const updateQuestionOptionAnswer = (
  questions,
  questionId,
  optionId,
  answer,
) => {
  return questions.map(q => {
    if (q.questionId === questionId) {
      return {
        ...q,
        answer: true,
        options: q.options.map(op => {
          if (op.optionId === optionId) {
            return {
              ...op,
              answer: answer,
            };
          }
          return op;
        }),
      };
    }
    return q;
  });
};
export const isDeveloperMode = environment => environment === 'development';

export const getColumnFilter = filterValue => {
  const userFilter = { ...filterValue };
  for (const [key, value] of Object.entries(filterValue)) {
    //For multi-select input, extract keys only
    if (Array.isArray(value)) {
      userFilter[key] = '';
      value.forEach(item => {
        //if production process was selected, then use memberKeys
        if (item.title) {
          userFilter[key] +=
            userFilter[key] === '' ? item.memberKeys : `,${item.memberKeys}`;
        } else {
          userFilter[key] += userFilter[key] === '' ? item.key : `,${item.key}`;
        }
      });
    }
    // For date range objects with from/to, expand to keyFrom and keyTo
    else if (
      typeof value === 'object' &&
      !Array.isArray(value) &&
      value !== null &&
      'from' in value &&
      'to' in value
    ) {
      delete userFilter[key];
      if (value.from) {
        userFilter[`${key}From`] = value.from;
      }
      if (value.to) {
        userFilter[`${key}To`] = value.to;
      }
    }
    //For single select input, extract key as filter value
    else if (
      typeof value === 'object' &&
      !Array.isArray(value) &&
      value !== null
    ) {
      userFilter[key] = !isNullOrUndefined(value.key) ? value.key : '';
    }
  }
  for (const [key, value] of Object.entries(userFilter)) {
    if (value === '') delete userFilter[key];
  }
  return userFilter;
};

export const parseAnswer = answer => {
  if (!answer) {
    return null;
  }

  if (isValidJson(answer)) {
    return JSON.parse(answer);
  }
  if (typeof answer === 'object') {
    return answer;
  }
  return answer;
};

export const processSupplierQuestionnaireAnswer = question => {
  let answer =
    Array.isArray(question?.answer) && question?.answer?.some(a => a.answer)
      ? question?.answer?.filter(a => a.answer)
      : (question?.answer ?? '');
  if (question.retracedDatapointId === 'supply_chain_tier')
    answer.value = question.answer.value.map(v =>
      typeof v === 'string' ? v : v.key,
    );

  switch (question?.answerType?.key) {
    case constants.companyDataFieldsTypes.enumMapping.CHECK_BOX:
      answer = {
        value: question.answer.value.map(option => ({
          key: option.key,
          isChecked: true,
          followUp: option.followUp?.value
            ? { value: option.followUp.value }
            : null,
        })),
      };
      break;

    case constants.companyDataFieldsTypes.enumMapping.NUMBER:
      answer.value = Number.parseInt(answer.value);
      break;

    case constants.companyDataFieldsTypes.enumMapping.SHORT_TEXT:
    case constants.companyDataFieldsTypes.enumMapping.LONG_TEXT:
      answer.value = answer.value.toString();
      break;

    case constants.companyDataFieldsTypes.enumMapping.NUMBER_UOM:
    case constants.companyDataFieldsTypes.enumMapping.NUMBER_DYNAMIC_UOM:
    case constants.companyDataFieldsTypes.enumMapping
      .NUMBER_DYNAMIC_UOM_WITH_YEAR:
    case constants.companyDataFieldsTypes.enumMapping.NUMBER_DYNAMIC_CURRENCY:
    case constants.companyDataFieldsTypes.enumMapping
      .NUMBER_DYNAMIC_CURRENCY_WITH_YEAR:
      answer = {
        value: question.value?.value
          ? Number(question.value.value)
          : Number(question.value),
        unit: question.unit,
        year: question.year,
      };
      break;

    default:
      break;
  }

  return answer;
};

export const getSupplierQuestionnaireQuestionAnswerStatus = question => {
  let status = 'Not submitted yet';
  let statusClassName = 'Missing';
  let color = 'gray-lighter';
  if (question?.reviewStatus) {
    status =
      constants.questionnaireResponseAnswerStatus.enumMapping[
        question.reviewStatus
      ];
    if (
      status !==
        constants.questionnaireResponseAnswerStatus.enumMapping.APPROVED &&
      status !==
        constants.questionnaireResponseAnswerStatus.enumMapping.DENIED &&
      (question?.isNotAvailable || !isNullOrUndefinedOrEmpty(question.answer))
    ) {
      status =
        constants.questionnaireResponseAnswerStatus.enumMapping.IN_REVIEW;
    }
    switch (status) {
      case constants.questionnaireResponseAnswerStatus.enumMapping.APPROVED:
        statusClassName = 'Approved';
        color = 'success';
        break;
      case constants.questionnaireResponseAnswerStatus.enumMapping.DENIED:
        statusClassName = 'Denied';
        color = 'danger';
        break;
      case constants.questionnaireResponseAnswerStatus.enumMapping.IN_REVIEW:
        statusClassName = 'In Review';
        color = 'warning';
        break;
      case constants.questionnaireResponseAnswerStatus.enumMapping
        .NOT_SUBMITTED:
      default:
        color = 'custom-gray';
        break;
    }
  }

  return {
    status,
    color,
    statusClassName,
  };
};

export const disabledPreviousDates = current => {
  const today = startOfDay(new Date());
  const currentDate = startOfDay(current.valueOf());
  return isBefore(currentDate, today);
};

export const customStringtoCamelCase = str => {
  if (!str) return '';
  return str
    .replace(/[^a-zA-Z0-9\s]+/g, '')
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]+(.)/g, (match, chr) => chr.toUpperCase());
};

export const generatePagination = paginationMetaData => {
  return {
    pageNumbers:
      Math.ceil(
        paginationMetaData?.totalCount / paginationMetaData?.pageSize,
      ) || 1,
    rowsPerPage: paginationMetaData?.pageSize,
    currentPage: paginationMetaData?.page + 1 || 0,
    totalCount: paginationMetaData?.totalCount,
    rowsPerPageOptions: ROWS_PER_PAGE_SELECTOR,
  };
};

export const getSavedTablePreference = (me, key, tablePreferencesKey) => {
  let savedPreference =
    me.preferences?.[key] && Object.keys(me.preferences[key]).length > 0
      ? JSON.stringify(me.preferences[key])
      : null;
  savedPreference =
    savedPreference == null ? [] : safeJsonParse(savedPreference, []);
  savedPreference = savedPreference.filter(
    c => c.table === tablePreferencesKey,
  );
  return savedPreference ? savedPreference[0] : null;
};

export const questionnaireDataPointTemplateStructure = [
  {
    id: 'GENERAL_INFORMATION',
    name: 'General Information',
    isParent: true,
    children: [
      {
        id: 'ABOUT',
        name: 'About',
        isParent: false,
      },
      {
        id: 'OTHER_IDS',
        name: 'Other Ids',
        isParent: false,
      },
      {
        id: 'OPERATIONAL_METRICS',
        name: 'Operational Metrics',
        isParent: false,
      },
    ],
  },
  {
    name: 'ESG',
    id: 'ESG',
    isParent: true,
    children: [
      {
        name: 'Environmental',
        id: 'ENVIRONMENTAL',
        isParent: true,
        children: [
          {
            name: 'Waste',
            id: 'WASTE',
            isParent: true,
            children: [
              { name: 'Waste Streams', id: 'WASTE_STREAMS', isParent: false },
            ],
          },
          {
            name: 'Water',
            id: 'WATER',
            isParent: true,
            children: [
              {
                name: 'Water Management',
                id: 'WATER_MANAGEMENT',
                isParent: false,
              },
              {
                name: 'Wastewater Management',
                id: 'WASTEWATER_MANAGEMENT',
                isParent: false,
              },
            ],
          },
          {
            name: 'Climate',
            id: 'CLIMATE',
            isParent: true,
            children: [
              { name: 'GHG Emissions', id: 'GHG_EMISSIONS', isParent: false },
            ],
          },
          {
            name: 'Energy',
            id: 'ENERGY',
            isParent: true,
            children: [
              { name: 'Energy sources', id: 'ENERGY_SOURCES', isParent: false },
            ],
          },
        ],
      },
      {
        name: 'Social',
        id: 'SOCIAL',
        isParent: true,
        children: [
          {
            name: 'Worker Involvement',
            id: 'WORKER_INVOLVEMENT',
            isParent: true,
            children: [
              {
                name: 'Workplace Cooperation',
                id: 'WORKPLACE_COOPERATION',
                isParent: false,
              },
              {
                name: 'Complaint & Grievance Systems',
                id: 'COMPLAINT_AND_GRIEVANCE_SYSTEMS',
                isParent: false,
              },
              {
                name: 'Freedom of Association & Collective Bargaining',
                id: 'FREEDOM_OF_ASSOCIATION_AND_COLLECTIVE_BARGAINING',
                isParent: false,
              },
            ],
          },
          {
            name: 'Employment Practices',
            id: 'EMPLOYMENT_PRACTICES',
            isParent: true,
            children: [
              {
                name: 'Equality & Diversity',
                id: 'EQUALITY_AND_DIVERSITY',
                isParent: false,
              },
              { name: 'Child Labor', id: 'CHILD_LABOR', isParent: false },
              {
                name: 'Contracts & Terms of Employment',
                id: 'CONTRACTS_AND_TERMS_OF_EMPLOYMENT',
                isParent: false,
              },
              { name: 'Discipline', id: 'DISCIPLINE', isParent: false },
              {
                name: 'Working Hours & Overtime',
                id: 'WORKING_HOURS_AND_OVERTIME',
                isParent: false,
              },
              {
                name: 'Wages & Benefits',
                id: 'WAGES_AND_BENEFITS',
                isParent: false,
              },
              {
                name: 'Vulnerable Groups',
                id: 'VULNERABLE_GROUP',
                isParent: false,
              },
              {
                name: 'General Work Environment',
                id: 'GENERAL_WORK_ENVIRONMENT',
                isParent: false,
              },
              {
                name: 'Recruitment, Hiring & Termination Practices',
                id: 'RECRUITMENT_HIRING_AND_TERMINATION_PRACTICES',
                isParent: false,
              },
              {
                name: 'Subcontracting & Homeworker',
                id: 'SUBCONTRACTING_AND_HOMEWORKER',
                isParent: false,
              },
            ],
          },
          {
            name: 'Above & Beyond',
            id: 'ABOVE_AND_BEYOND',
            isParent: true,
            children: [
              { name: 'Child Care', id: 'CHILD_CARE', isParent: false },
              {
                name: 'Accommodation & Dormitories',
                id: 'ACCOMODATION_AND_DORMITORIES',
                isParent: false,
              },
              { name: 'Canteens', id: 'CANTEENS', isParent: false },
              {
                name: 'Worker Wellbeing',
                id: 'WORKER_WELLBEING',
                isParent: false,
              },
              {
                name: 'Community Engagement',
                id: 'COMMUNITY_ENGAGEMENT',
                isParent: false,
              },
              {
                name: 'Community Impact',
                id: 'COMMUNITY_IMPACT',
                isParent: false,
              },
            ],
          },
          {
            name: 'Worker Treatment',
            id: 'WORKER_TREATMENT',
            isParent: true,
            children: [
              { name: 'Forced Labour', id: 'FORCED_LABOUR', isParent: false },
              {
                name: 'Harassment & Abuse',
                id: 'HARRASSMENT_AND_ABUSE',
                isParent: false,
              },
              { name: 'Discrimination', id: 'DISCRIMINATION', isParent: false },
            ],
          },
          {
            name: 'Health & Safety',
            id: 'HEALTH_AND_SAFETY',
            isParent: true,
            children: [
              {
                name: 'Risk Assessment',
                id: 'RISK_ASSESSMENT',
                isParent: false,
              },
              {
                name: 'Flammable & Combustible Materials',
                id: 'FLAMMABLE_AND_COMBUSTIBLE_MATERIALS',
                isParent: false,
              },
              {
                name: 'Engagement with OHS',
                id: 'ENGAGEMENT_WITH_OHS',
                isParent: false,
              },
              {
                name: 'Emergency Preparedness',
                id: 'EMERGENCY_PREPAREDNESS',
                isParent: false,
              },
              {
                name: 'Chemicals & Hazardous Substances',
                id: 'CHEMICALS_AND_HAZARDOUS_SUBSTANCES',
                isParent: false,
              },
              {
                name: 'Materials Handling & Storage',
                id: 'MATERIALS_HANDLING_AND_STORAGE',
                isParent: false,
              },
              {
                name: 'Worker Protection',
                id: 'WORKER_PROTECTION',
                isParent: false,
              },
              {
                name: 'Safety Warnings',
                id: 'SAFETY_WARNINGS',
                isParent: false,
              },
              {
                name: 'Electrical Safety',
                id: 'ELECTRICAL_SAFETY',
                isParent: false,
              },
            ],
          },
        ],
      },
      {
        name: 'Governance',
        id: 'GOVERNANCE',
        isParent: true,
        children: [
          {
            name: 'Organisation Profile',
            id: 'ORGANISATION_PROFILE',
            isParent: true,
            children: [
              {
                name: 'Responsible Business Conduct',
                id: 'RESPONSIBLE_BUSINESS_CONDUCT',
                isParent: false,
              },
              {
                name: 'Worker Demographics',
                id: 'WORKER_DEMOGRAPHICS',
                isParent: false,
              },
              {
                name: 'Building & Operating Licenses',
                id: 'BUILDING_AND_OPERATING_LICENSES',
                isParent: false,
              },
              {
                name: 'Structures & Processes',
                id: 'STRUCTURES_AND_PROCESSES',
                isParent: false,
              },
              {
                name: 'Roles & Responsibilities',
                id: 'ROLES_AND_RESPONSIBILITIES',
                isParent: false,
              },
            ],
          },
          {
            name: 'Implementing Sustainability',
            id: 'IMPLEMENTING_SUSTAINABILITY',
            isParent: true,
            children: [
              {
                name: 'Management Systems',
                id: 'MANAGEMENT_SYSTEMS',
                isParent: false,
              },
            ],
          },
          {
            name: 'Due Diligence',
            id: 'DUE_DILIGENCE',
            isParent: true,
            children: [
              {
                name: 'Policies & Procedures',
                id: 'POLICIES_AND_PROCEDURES',
                isParent: false,
              },
            ],
          },
          {
            name: 'Transparency',
            id: 'TRANSPARENCY',
            isParent: true,
            children: [
              { name: 'Transparency', id: 'TRANSPARENCY', isParent: false },
            ],
          },
        ],
      },
    ],
  },
];

/**
 * Accepts the pure response data from api.companies.getCompanyProfileById(TEMPLATE_ID, FOR_COMPANY_ID)
 * and returns it in a way that old components can very easily use
 * @param {object} responseData
 * @returns {object}
 */
export const formatCompaniesApiResponseToOldFields = responseData => {
  const company = {
    id: responseData.forCompanyId,
    createdAt: responseData.createdAt,
    updatedAt: responseData.updatedAt,
    isConnected: responseData.isConnected,
    isReverseConnected: responseData.isReverseConnected,
  };

  /* eslint-disable */
  const fieldMapping = {
    address_line_1: 'addressLine1',
    address_line_2: 'addressLine2',
    city: 'city',
    company_size: 'companySize',
    country: 'country',
    country_name: 'countryName',
    description: 'description',
    display_name: 'name',
    phone: 'phone',
    website: 'website',
    zip_code: 'zipCode',

    commercial_registration_number: 'commercialRegisterNumber',
    global_location_number: 'globalLocationNumber',
    duns_number: 'dunsNumber',
    higg_id: 'higgId',
    os_hub_id: 'osHubId',
    vat_id: 'vatId',

    annual_production_capacity: 'annualProductionCapacity',
    minimum_order_quantity_per_style: 'minimumOrderQuantityPerStyle',
    minimum_order_quantity: 'minimumOrderQuantity',
    number_of_employees: 'numOfPermanentEmployees',
    number_of_female_employees: 'numOfFemaleEmployees',
    number_of_male_employees: 'numOfMaleEmployees',
    standard_lead_time: 'standardLeadTime',
    /* eslint-enable */
  };

  const dataPoints = responseData.data;

  Object.keys(dataPoints).forEach(k => {
    const dataPoint = dataPoints[k];
    if (!dataPoint) return;

    if (k in fieldMapping) {
      company[fieldMapping[k]] = dataPoint.value;
    }

    if (k === 'connection_type' && dataPoint.value) {
      company.extend = { connectionType: dataPoint.value };
    }

    if (
      k === 'facility_processes' &&
      Array.isArray(dataPoint.value) &&
      dataPoint.value.length > 0
    ) {
      company.facilityProcesses = dataPoint.value;
    }

    if (k === 'image_list' && dataPoint.value?.length > 0) {
      company.imageList = dataPoint.value;
      company.featuredImageFileId = dataPoint.value[0]?.uploadFileId;
      company.featuredImageFileUrl = dataPoint.value[0]?.url;
    }

    if (k === 'video_file' && dataPoint.value) {
      company.videoFileUrl = dataPoint.value?.url;
    }
  });

  return company;
};

export function setAllowedCookiesFlag(value) {
  localStorage.setItem('allowedIntercomCookies', value);
}

export function getAllowedCookiesFlag() {
  return localStorage.getItem('allowedIntercomCookies') === 'true';
}

export function checkIfUserHasAllowedCookies() {
  return localStorage.getItem('allowedIntercomCookies');
}

export const getAuthStatus = (
  accessToken,
  refreshToken,
  me,
  sessionStartedAt,
  hasSessionExpired,
) => {
  const isCookieEnabled = config.isCookieEnabled;
  const isAccessTokenValid = !isTokenExpired(accessToken);
  const isRefreshTokenValid = !isTokenExpired(refreshToken);
  const isSessionValid = !isSessionExpired(sessionStartedAt);
  const isMeValid = me?.id;

  if (isCookieEnabled) {
    // Users that have accessToken and/or refreshToken in their localstorage
    // gracefully saying session expired for them so that they re-login with cookies
    // Remove this after 1 week of production deployment (first check)
    if (accessToken || refreshToken) {
      return 'SessionExpired';
    }
    if (!sessionStartedAt) {
      return 'Unauthenticated';
    }
    if (!isSessionValid && hasSessionExpired) {
      return 'SessionExpired';
    }
    if (!isSessionValid) {
      return 'RenewingToken';
    }
    return isMeValid ? 'Authorized' : 'FetchingMe';
  }

  if (!isAccessTokenValid && isRefreshTokenValid) {
    return 'RenewingToken';
  }
  if (!isAccessTokenValid && refreshToken && !isRefreshTokenValid) {
    return 'SessionExpired';
  }
  if (!isAccessTokenValid && !isRefreshTokenValid) {
    return 'Unauthenticated';
  }
  if (isAccessTokenValid) {
    return isMeValid ? 'Authorized' : 'FetchingMe';
  }

  return 'Invalid';
};

export const getLocaleDate = date => format(new Date(date), 'MMM dd, yyyy');

export const getMonthsDifference = (startDate, endDate) => {
  const comparedStartDate = new Date(startDate);
  const comparedEndDate = new Date(endDate);
  comparedEndDate.setDate(comparedEndDate.getDate() + 1);
  const monthsDiff =
    (comparedEndDate.getFullYear() - comparedStartDate.getFullYear()) * 12 +
    (comparedEndDate.getMonth() - comparedStartDate.getMonth());

  return `${monthsDiff}`;
};

export function isGlobalDataPoint(retracedDataPointId) {
  if (!retracedDataPointId) return false;
  const dp = dataPoints.getDataPointById(retracedDataPointId);
  return (
    dp && dp.dataPointDefinition && dp.dataPointDefinition.type === 'GLOBAL'
  );
}

export function getFilterFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const filter = params.get('filter');
  return safeJsonParse(filter);
}

export function safeJsonParse(jsonString, fallback = {}) {
  if (!jsonString) {
    return fallback;
  }

  try {
    return JSON.parse(jsonString);
  } catch (error) {
    console.warn('Failed to parse JSON:', error);
    return fallback;
  }
}

export function roundToTwoDecimals(num) {
  return num % 1 !== 0 ? Number.parseFloat(num.toFixed(2)) : num;
}
